import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Ban, CheckCircle2, Gavel, Loader2, ScrollText, TriangleAlert, UserCheck, UserPlus, Users, Vote } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import { inputClass } from '../components/fields';
import {
  cancelMeeting, castVote, closeBallot, concludeMeeting, getBallot, getMeeting, listMotions, openBallot,
  publishMeeting, registerAttendance, startMeeting, submitMotion, withdrawMotion, placeMotionOnAgenda,
  requestSecrecy, grantProxy, revokeProxy, getProxyContext,
  getMeetingMinutes, compileMinutes, editMinutes, publishMinutes, addAddendum,
  type AgendaItem, type BallotKind, type BallotView, type MeetingDetail as Meeting, type MotionRow, type ProxyContext, type MinutesView,
} from '../lib/meetings';
import type { Me } from '../lib/session';

type Load = 'loading' | 'ready' | 'error';
type Notice = { tone: 'success' | 'error'; text: string } | null;

/** Spec 005 — the meeting workspace: notice + agenda + motions + the voting core + board controls.
 *  Every interactive surface shows the four states (loading / empty / specific error / success). */
function MeetingDetailInner({ user }: { user: Me }) {
  const { id = '' } = useParams();
  const isBoard = user.role === 'board' || user.role === 'admin';
  const [load, setLoad] = useState<Load>('loading');
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [motions, setMotions] = useState<MotionRow[]>([]);
  const [ballots, setBallots] = useState<Record<string, BallotView | null>>({});
  const [notice, setNotice] = useState<Notice>(null);
  const [attending, setAttending] = useState(false);
  // Spec 005g — viewer-scoped proxy state for the proxy panel + on-behalf voting. `undefined` = loading.
  const [proxyCtx, setProxyCtx] = useState<ProxyContext | null | undefined>(undefined);
  // Spec 005h — the meeting's minutes for the minutes panel. `undefined` = loading, `null` = none the
  // viewer may see (no minutes yet, or a draft a plain member must not see, SC-536).
  const [minutes, setMinutes] = useState<MinutesView | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    const [m, mo] = await Promise.all([getMeeting(id), listMotions(id)]);
    if (m.kind === 'ok') {
      setMeeting(m.data);
      // Spec 005c (F8): seed live ballots from the agenda items the API embedded for participants,
      // so a member who reloads a live meeting sees the open ballot and can vote — not a blank item.
      setBallots(Object.fromEntries(m.data.agendaItems.map((i) => [i.id, i.ballot ?? null])));
      const withMotions = mo.kind === 'ok' ? mo.data : [];
      setMotions(withMotions);
      setLoad('ready');
      // Spec 005g: proxy context loads INDEPENDENTLY so it never blocks the page and the panel owns its
      // own loading state. Best-effort — the panel hides itself if it can't load. Previous value is kept
      // while re-fetching (no flicker on a post-mutation refresh); initial state is `undefined` = loading.
      // It also carries the server's truth about presence, so a present member who RELOADS a live meeting
      // sees their vote controls (own + on-behalf) without re-clicking "register attendance" (F8 spirit).
      void getProxyContext(id).then((pc) => {
        if (pc.kind === 'ok') {
          setProxyCtx(pc.data);
          if (pc.data.viewerPresent) setAttending(true);
        } else {
          setProxyCtx(null);
        }
      });
      // Spec 005h: minutes load INDEPENDENTLY too, so the panel owns its loading state and a failed
      // fetch degrades to "none visible" rather than blocking the page. `null` covers both no-minutes
      // and a draft a plain member may not see — the UI renders one uniform "not published yet" state.
      void getMeetingMinutes(id).then((mn) => setMinutes(mn.kind === 'ok' ? mn.data : null));
    } else if (m.kind === 'forbidden') {
      setLoad('error');
      setNotice({ tone: 'error', text: 'This meeting hasn’t been published yet.' });
    } else {
      setLoad('error');
    }
  }, [id]);
  useEffect(() => { void refresh(); }, [refresh]);

  function flash(n: Notice) { setNotice(n); }

  async function onAttend() {
    setNotice(null);
    const r = await registerAttendance(id);
    // Spec 005c (F8): re-fetch after attending so any already-open ballots load and become votable.
    if (r.kind === 'ok') { setAttending(true); flash({ tone: 'success', text: 'You’re registered as present. You can now vote on open ballots.' }); void refresh(); }
    else if (r.kind === 'conflict') flash({ tone: 'error', text: r.message });
    else if (r.kind === 'forbidden') flash({ tone: 'error', text: r.message });
    else flash({ tone: 'error', text: 'We couldn’t register your attendance. Please try again.' });
  }

  async function lifecycle(action: 'publish' | 'start' | 'conclude') {
    setNotice(null);
    const fn = action === 'publish' ? publishMeeting : action === 'start' ? startMeeting : concludeMeeting;
    const r = await fn(id);
    if (r.kind === 'ok') { flash({ tone: 'success', text: `Meeting ${action === 'publish' ? 'published' : action === 'start' ? 'started' : 'concluded'}.` }); void refresh(); }
    else if (r.kind === 'conflict') flash({ tone: 'error', text: r.message }); // e.g. the §12 three-week message
    else if (r.kind === 'forbidden') flash({ tone: 'error', text: 'Only the board can do that.' });
    else flash({ tone: 'error', text: 'That action didn’t go through. Please try again.' });
  }

  if (load === 'loading') {
    return <div className="mx-auto max-w-3xl px-4 py-16 text-center" aria-live="polite"><Loader2 aria-hidden className="mx-auto h-8 w-8 animate-spin text-accent" /><p className="mt-4 font-serif text-lg text-ink">Loading meeting…</p></div>;
  }
  if (load === 'error' || !meeting) {
    return <div className="mx-auto max-w-3xl px-4 py-16"><p role="alert" className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error"><TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />{notice?.text ?? 'We couldn’t load this meeting.'}</p></div>;
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-12">
      <header>
        <p className="font-display text-xs uppercase tracking-[0.2em] text-accent-deep">{meeting.type === 'annual' ? 'Annual meeting' : 'Extra meeting'} · {meeting.status.replace('_', ' ')}</p>
        <h1 className="mt-1 font-display text-3xl font-semibold uppercase tracking-[0.06em] text-ink">{meeting.title}</h1>
        <p className="mt-2 font-serif text-base text-muted">{new Date(meeting.scheduledAt).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })}{meeting.location ? ` · ${meeting.location}` : ''}</p>
      </header>

      {/* Spec 005i — the cancelled resting state: an unmistakable, persistent banner for every viewer
          (board and member). The record below stays visible read-only; no live controls are offered. */}
      {meeting.status === 'cancelled' && (
        <p role="status" className="mt-6 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          This meeting has been cancelled. Any open ballots were voided; the record below is kept for the association’s history.
        </p>
      )}

      {notice && (
        <p role={notice.tone === 'error' ? 'alert' : 'status'} className={`mt-6 flex items-start gap-2 rounded-md border px-3 py-2 font-sans text-sm ${notice.tone === 'error' ? 'border-error/40 bg-error/5 text-error' : 'border-success/40 bg-success/5 text-success'}`}>
          {notice.tone === 'error' ? <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}{notice.text}
        </p>
      )}

      {/* Board lifecycle + cancel controls — never on a terminal (concluded/cancelled) meeting.
          Spec 005i: the destructive "Cancel meeting" sits here on published/in_progress only. */}
      {isBoard && meeting.status !== 'concluded' && meeting.status !== 'cancelled' && (
        <div className="mt-6 flex flex-wrap gap-2">
          {meeting.status === 'draft' && <BoardBtn onClick={() => lifecycle('publish')} icon={ScrollText}>Publish notice</BoardBtn>}
          {meeting.status === 'published' && <BoardBtn onClick={() => lifecycle('start')} icon={Gavel}>Start meeting</BoardBtn>}
          {meeting.status === 'in_progress' && <BoardBtn onClick={() => lifecycle('conclude')} icon={CheckCircle2}>Conclude meeting</BoardBtn>}
          {(meeting.status === 'published' || meeting.status === 'in_progress') && (
            <CancelControl meetingId={id} onChanged={refresh} flash={flash} />
          )}
        </div>
      )}

      {/* Attendance (member, while in progress) */}
      {meeting.status === 'in_progress' && (
        <div className="mt-6 rounded-md border border-accent/40 bg-accent/5 p-4">
          <button type="button" onClick={onAttend} disabled={attending} className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
            <UserCheck aria-hidden className="h-4 w-4" /> {attending ? 'You’re present' : 'Register attendance'}
          </button>
          <p className="mt-2 font-sans text-xs text-muted">You must be present to vote (§14.1). Registering in person voids any proxy you granted.</p>
        </div>
      )}

      {/* Proxy delegation (member, §14.2) — grant/revoke; the on-behalf vote lives in each agenda row. */}
      {(meeting.status === 'published' || meeting.status === 'in_progress') && (
        <ProxyPanel meetingId={id} ctx={proxyCtx} onChanged={refresh} flash={flash} />
      )}

      {/* Agenda + ballots */}
      <h2 className="mt-10 font-display text-sm font-semibold uppercase tracking-[0.14em] text-ink">Agenda</h2>
      {meeting.agendaItems.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-border bg-surface/40 px-4 py-8 text-center font-serif text-muted">The agenda hasn’t been set yet.</p>
      ) : (
        <ol className="mt-4 space-y-3">
          {meeting.agendaItems.map((item) => (
            <AgendaRow key={item.id} item={item} meeting={meeting} isBoard={isBoard} attending={attending}
              ballot={ballots[item.id]} setBallot={(b) => setBallots((s) => ({ ...s, [item.id]: b }))} flash={flash}
              proxiesHeld={proxyCtx?.proxiesHeld ?? []} />
          ))}
        </ol>
      )}
      {/* SC-540 — a live meeting with nothing to vote on is a real, visible state, not a blank agenda. */}
      {meeting.status === 'in_progress' && !meeting.agendaItems.some((item) => ballots[item.id]?.state === 'open') && (
        <p data-testid="no-open-ballots" className="mt-3 rounded-md border border-dashed border-border bg-surface/40 px-4 py-8 text-center font-serif text-muted">No open ballots right now.</p>
      )}

      {/* Motions */}
      <MotionsSection meetingId={id} meeting={meeting} motions={motions} isBoard={isBoard} onChange={refresh} flash={flash} />

      {/* Minutes (§14.4) — visible once concluded (board) or whenever published minutes exist (all). */}
      {(meeting.status === 'concluded' || (minutes != null)) && (
        <MinutesSection meetingId={id} minutes={minutes} isBoard={isBoard} onChanged={refresh} flash={flash} />
      )}
    </section>
  );
}

function BoardBtn({ onClick, icon: Icon, children }: { onClick: () => void; icon: typeof Gavel; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-2 rounded-md border border-accent/50 bg-surface px-3.5 py-2 font-sans text-sm font-semibold text-ink transition-colors duration-150 ease-standard hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
      <Icon aria-hidden className="h-4 w-4 text-accent" /> {children}
    </button>
  );
}

/** Spec 005i — the destructive meeting-cancel control (§11–§12). Cancelling voids any open ballots and
 *  is IRREVERSIBLE (cancelled is terminal), so this is a deliberate TWO-STEP inline confirm, never a
 *  single click: the first click only opens the red-washed confirm panel — no server call — and only
 *  "Confirm cancellation" invokes the server. The visual hierarchy is inverted on purpose: the SAFE
 *  choice ("Keep meeting") is the quiet neutral button and the destructive one is red, so muscle memory
 *  lands on safety. Reuses the ConfirmErase (spec 004) destructive grammar. Four states: success
 *  (cancelled → the resting banner), specific error (server refusal verbatim, e.g. a terminal meeting
 *  from a stale client — panel stays open on a transient error to retry), loading (confirm in flight).
 *  Trust-but-verify: the server voids the ballots and owns every rule; this control reports, never
 *  re-implements. */
function CancelControl({ meetingId, onChanged, flash }: {
  meetingId: string; onChanged: () => Promise<void>; flash: (n: Notice) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  // A11y: move focus into the confirm panel (the reason field) when it opens.
  useEffect(() => { if (confirming) reasonRef.current?.focus(); }, [confirming]);

  function dismiss() { if (busy) return; setConfirming(false); setReason(''); }

  async function onConfirm() {
    if (busy) return;
    setBusy(true); flash(null);
    const r = await cancelMeeting(meetingId, reason.trim() || undefined);
    setBusy(false);
    if (r.kind === 'ok') {
      setConfirming(false); setReason('');
      flash({ tone: 'success', text: 'Meeting cancelled. Any open ballots were voided.' });
      void onChanged();
    } else if (r.kind === 'conflict' || r.kind === 'forbidden') {
      flash({ tone: 'error', text: r.message }); // terminal / not board — verbatim; reconcile to true state
      void onChanged();
    } else if (r.kind === 'invalid') {
      flash({ tone: 'error', text: Object.values(r.errors)[0] ?? 'Please check the cancellation reason.' });
    } else {
      flash({ tone: 'error', text: 'We couldn’t cancel the meeting. Please try again.' }); // panel stays open to retry
    }
  }

  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)}
        className="inline-flex items-center gap-2 rounded-md border border-error/60 bg-surface px-3.5 py-2 font-sans text-sm font-semibold text-error transition-colors duration-150 ease-standard hover:border-error hover:bg-error/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error">
        <Ban aria-hidden className="h-4 w-4" /> Cancel meeting
      </button>
    );
  }

  return (
    <div role="group" aria-label="Confirm meeting cancellation" onKeyDown={(e) => { if (e.key === 'Escape') dismiss(); }}
      className="w-full rounded-md border border-error/40 bg-error/5 p-4">
      <p className="flex items-start gap-2 font-display text-sm font-semibold uppercase tracking-[0.1em] text-error">
        <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" /> This cannot be undone
      </p>
      <p className="mt-2 font-serif text-sm leading-relaxed text-ink">
        Cancelling this meeting voids any open ballots and moves it to a cancelled state. The record and any minutes are kept.
      </p>
      <label htmlFor="cancel-reason" className="mt-3 block font-sans text-sm font-medium text-ink">Reason <span className="font-normal text-muted">(optional)</span></label>
      <textarea id="cancel-reason" ref={reasonRef} maxLength={500} rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Why is this meeting being cancelled?" className={`${inputClass} mt-1.5`} />
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={onConfirm} disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-error bg-error px-3.5 py-2 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error">
          {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Confirm cancellation
        </button>
        <button type="button" onClick={dismiss} disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3.5 py-2 font-sans text-sm font-semibold text-ink transition-colors duration-150 ease-standard hover:border-accent disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
          Keep meeting
        </button>
      </div>
    </div>
  );
}

/** Spec 005g — proxy delegation panel (§14.2). Four states: loading (context resolving), success
 *  (delegated / revoked, named), specific error (server §14 refusal, verbatim), empty (no eligible
 *  holder present). Trust-but-verify: the server is authoritative; the picker is only a convenience. */
function ProxyPanel({ meetingId, ctx, onChanged, flash }: {
  meetingId: string; ctx: ProxyContext | null | undefined; onChanged: () => Promise<void>; flash: (n: Notice) => void;
}) {
  const [holder, setHolder] = useState('');
  const [busy, setBusy] = useState(false);

  // Loading — the viewer's proxy context (candidates + own delegation) hasn't resolved yet.
  if (ctx === undefined) {
    return (
      <div className="mt-6 rounded-md border border-border bg-surface p-4" aria-live="polite">
        <p className="inline-flex items-center gap-2 font-sans text-sm text-muted"><Loader2 aria-hidden className="h-4 w-4 animate-spin text-accent" /> Loading proxy options…</p>
      </div>
    );
  }
  // Couldn't load, or nothing to show for this viewer (present with no delegation → they vote themselves).
  if (ctx === null) return null;
  const showGrant = !ctx.viewerPresent && !ctx.myDelegation && ctx.delegable;
  if (!ctx.myDelegation && !showGrant) return null;

  async function onGrant() {
    if (!holder || busy) return;
    setBusy(true); flash(null);
    const r = await grantProxy(meetingId, holder);
    setBusy(false);
    if (r.kind === 'ok') { setHolder(''); flash({ tone: 'success', text: 'Your vote is delegated. You can revoke it any time before it is cast.' }); void onChanged(); }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message }); // §14 refusal, verbatim
    else if (r.kind === 'invalid') flash({ tone: 'error', text: Object.values(r.errors)[0] ?? 'Please choose a member to hold your proxy.' });
    else flash({ tone: 'error', text: 'We couldn’t delegate your vote. Please try again.' });
  }
  async function onRevoke() {
    if (!ctx?.myDelegation || busy) return;
    setBusy(true); flash(null);
    const r = await revokeProxy(ctx.myDelegation.proxyId);
    setBusy(false);
    if (r.kind === 'ok') { flash({ tone: 'success', text: 'Proxy revoked. Your vote is your own again.' }); void onChanged(); }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message }); // already exercised / not yours
    else flash({ tone: 'error', text: 'We couldn’t revoke the proxy. Please try again.' });
  }

  return (
    <div className="mt-6 rounded-md border border-border bg-surface p-4">
      <h2 className="inline-flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.14em] text-ink"><Users aria-hidden className="h-4 w-4 text-accent" /> Proxy (§14.2)</h2>

      {ctx.myDelegation ? (
        <div className="mt-3">
          <p className="font-serif text-base text-ink">You have delegated your vote to <span className="font-semibold">{ctx.myDelegation.holderName}</span>.</p>
          <p className="mt-1 font-sans text-xs text-muted">Revoke to vote yourself. Registering your attendance in person also voids it.</p>
          <button type="button" onClick={onRevoke} disabled={busy} className="mt-3 inline-flex items-center gap-2 rounded-md border border-accent/50 bg-surface px-3.5 py-2 font-sans text-sm font-semibold text-ink hover:border-accent disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
            {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Revoke proxy
          </button>
        </div>
      ) : ctx.candidates.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-border bg-surface/40 px-4 py-6 text-center font-serif text-muted">No present member is currently available to hold your proxy.</p>
      ) : (
        <div className="mt-3">
          <p className="font-sans text-sm text-muted">Can’t attend? Delegate your vote to a member who is present (each may hold one proxy, §14.2).</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="proxy-holder">Member to hold your proxy</label>
            <select id="proxy-holder" value={holder} onChange={(e) => setHolder(e.target.value)} className={`${inputClass} w-auto`}>
              <option value="">Choose a member…</option>
              {ctx.candidates.map((c) => <option key={c.accountId} value={c.accountId}>{c.displayName}</option>)}
            </select>
            <button type="button" onClick={onGrant} disabled={busy || !holder} className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
              {busy ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <UserPlus aria-hidden className="h-4 w-4" />} Delegate my vote
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** One agenda item with its ballot: open (board) → cast (member, four states) → close (board) → result.
 *  Spec 005g: a member who holds a proxy also gets a distinct, clearly-labelled on-behalf vote group. */
function AgendaRow({ item, meeting, isBoard, attending, ballot, setBallot, flash, proxiesHeld }: {
  item: AgendaItem; meeting: Meeting; isBoard: boolean; attending: boolean;
  ballot: BallotView | null | undefined; setBallot: (b: BallotView | null) => void; flash: (n: Notice) => void;
  proxiesHeld: ProxyContext['proxiesHeld'];
}) {
  const [kind, setKind] = useState<BallotKind>('simple');
  const [busy, setBusy] = useState(false);
  const live = meeting.status === 'in_progress';

  const loadBallot = useCallback(async (ballotId: string) => {
    const r = await getBallot(ballotId);
    if (r.kind === 'ok') setBallot(r.data);
  }, [setBallot]);

  async function onOpen() {
    setBusy(true); flash(null);
    const r = await openBallot(item.id, { kind, secrecy: kind === 'election' && item.secrecyRequested ? 'secret' : 'open' });
    setBusy(false);
    if (r.kind === 'ok') setBallot(r.data);
    else if (r.kind === 'conflict') flash({ tone: 'error', text: r.message }); // quorum / secrecy-required
    else flash({ tone: 'error', text: 'We couldn’t open the ballot. Please try again.' });
  }
  async function onCast(choice: 'yes' | 'no' | 'abstain', onBehalfOf?: string, grantorName?: string) {
    if (!ballot) return;
    setBusy(true); flash(null);
    const r = await castVote(ballot.id, choice, onBehalfOf);
    setBusy(false);
    if (r.kind === 'ok') {
      flash({ tone: 'success', text: onBehalfOf ? `${grantorName}’s vote (${choice}) is recorded by proxy.` : `Your vote (${choice}) is recorded.` });
      void loadBallot(ballot.id);
    }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message }); // already voted / not present / no proxy / closed
    else flash({ tone: 'error', text: 'Your vote didn’t go through. Please try again.' });
  }
  async function onClose() {
    if (!ballot) return;
    setBusy(true); flash(null);
    const r = await closeBallot(ballot.id);
    setBusy(false);
    if (r.kind === 'ok') { setBallot(r.data); flash({ tone: 'success', text: `Ballot closed — result: ${r.data.result}.` }); }
    else flash({ tone: 'error', text: 'We couldn’t close the ballot. Please try again.' });
  }
  async function onRequestSecrecy() {
    const r = await requestSecrecy(item.id);
    if (r.kind === 'ok') flash({ tone: 'success', text: 'A secret ballot has been requested for this election.' });
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message });
  }

  return (
    <li className="rounded-md border border-border bg-surface p-4">
      <div className="flex items-start gap-3">
        <span className="font-display text-sm font-semibold text-accent-deep">{item.position}.</span>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-base text-ink">{item.title}</p>

          {/* Ballot open (board) */}
          {isBoard && live && !ballot && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor={`kind-${item.id}`}>Ballot kind</label>
              <select id={`kind-${item.id}`} value={kind} onChange={(e) => setKind(e.target.value as BallotKind)} className={`${inputClass} w-auto`}>
                <option value="simple">Simple majority</option>
                <option value="qualified">Qualified 2/3 (§24/§25)</option>
                <option value="election">Election (§14.1)</option>
              </select>
              <button type="button" onClick={onOpen} disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-1.5 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
                {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Open ballot
              </button>
            </div>
          )}

          {/* Secrecy request (member, election item, no ballot yet) */}
          {!isBoard && live && !ballot && item.kind === 'motion' && (
            <button type="button" onClick={onRequestSecrecy} className="mt-2 font-sans text-xs text-accent-deep underline underline-offset-2 hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">Request a secret ballot</button>
          )}

          {/* Ballot open — cast (member) */}
          {ballot && ballot.state === 'open' && (
            <div className="mt-3">
              <TallyBar tally={ballot.tally} secret={ballot.secrecy === 'secret'} />
              {!isBoard && (
                attending ? (
                  <>
                    <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Cast your vote">
                      {(['yes', 'no', 'abstain'] as const).map((c) => (
                        <button key={c} type="button" onClick={() => onCast(c)} disabled={busy} className="rounded-md border border-border bg-bg px-4 py-1.5 font-sans text-sm font-semibold capitalize text-ink transition-colors duration-150 ease-standard hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60">{c}</button>
                      ))}
                    </div>
                    {/* Spec 005g — on-behalf vote: a distinct, clearly-labelled group per held proxy, so casting
                        another member's vote is a deliberate, separate act (never auto-cast). One click each. */}
                    {proxiesHeld.map((p) => (
                      <div key={p.proxyId} className="mt-3 rounded-md border border-accent/40 bg-accent/5 p-3" role="group" aria-label={`Cast ${p.grantorName}’s vote by proxy`}>
                        <p className="inline-flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-[0.12em] text-accent-deep"><Users aria-hidden className="h-3.5 w-3.5" /> {p.grantorName}’s vote · by proxy</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(['yes', 'no', 'abstain'] as const).map((c) => (
                            <button key={c} type="button" onClick={() => onCast(c, p.grantorAccountId, p.grantorName)} disabled={busy} className="rounded-md border border-border bg-bg px-4 py-1.5 font-sans text-sm font-semibold capitalize text-ink transition-colors duration-150 ease-standard hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60">{c}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="mt-2 font-sans text-xs text-muted">Register your attendance above to vote on this item.</p>
                )
              )}
              {isBoard && <button type="button" onClick={onClose} disabled={busy} className="mt-2 inline-flex items-center gap-2 rounded-md border border-accent/50 px-3 py-1.5 font-sans text-sm font-semibold text-ink hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">Close ballot</button>}
            </div>
          )}

          {/* Ballot closed — result */}
          {ballot && ballot.state === 'closed' && (
            <div className="mt-3">
              <TallyBar tally={ballot.tally} secret={ballot.secrecy === 'secret'} />
              <p className="mt-2 inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-ink"><Vote aria-hidden className="h-4 w-4 text-accent" /> Result: {ballot.result}</p>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function TallyBar({ tally, secret }: { tally: { yes: number; no: number; abstain: number }; secret: boolean }) {
  return (
    <p className="font-sans text-sm text-muted" aria-live="polite">
      {secret && <span className="mr-2 rounded bg-accent/10 px-1.5 py-0.5 text-xs font-medium text-accent-deep">Secret</span>}
      <span className="font-semibold text-success">Yes {tally.yes}</span> · <span className="font-semibold text-error">No {tally.no}</span> · <span>Abstain {tally.abstain}</span>
    </p>
  );
}

function MotionsSection({ meetingId, meeting, motions, isBoard, onChange, flash }: {
  meetingId: string; meeting: Meeting; motions: MotionRow[]; isBoard: boolean; onChange: () => Promise<void>; flash: (n: Notice) => void;
}) {
  const [form, setForm] = useState({ title: '', body: '' });
  const [busy, setBusy] = useState(false);
  const canSubmit = meeting.status === 'published' && Date.now() < new Date(meeting.motionDeadline).getTime();

  async function onSubmit() {
    if (busy || !form.title.trim() || !form.body.trim()) return;
    setBusy(true); flash(null);
    const r = await submitMotion(meetingId, { title: form.title.trim(), body: form.body.trim() });
    setBusy(false);
    if (r.kind === 'ok') { setForm({ title: '', body: '' }); flash({ tone: 'success', text: 'Your motion was submitted.' }); void onChange(); }
    else if (r.kind === 'invalid') flash({ tone: 'error', text: Object.values(r.errors)[0] ?? 'Please check your motion.' });
    else if (r.kind === 'conflict') flash({ tone: 'error', text: r.message }); // deadline passed
    else flash({ tone: 'error', text: 'Your motion didn’t go through. Please try again.' });
  }
  async function onWithdraw(id: string) {
    const r = await withdrawMotion(id);
    if (r.kind === 'ok') { flash({ tone: 'success', text: 'Motion withdrawn.' }); void onChange(); }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message });
  }
  async function onPlace(id: string) {
    const r = await placeMotionOnAgenda(id);
    if (r.kind === 'ok') { flash({ tone: 'success', text: 'Motion placed on the agenda.' }); void onChange(); }
    else if (r.kind === 'conflict') flash({ tone: 'error', text: r.message });
  }

  return (
    <div className="mt-10">
      <h2 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-ink">Motions (§13)</h2>
      {canSubmit && (
        <div className="mt-3 rounded-md border border-border bg-surface p-4">
          <input type="text" maxLength={200} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Motion title" className={inputClass} />
          <textarea maxLength={10000} rows={3} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="What do you propose, and why?" className={`${inputClass} mt-2`} />
          <button type="button" onClick={onSubmit} disabled={busy} className="mt-3 inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
            {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Submit motion
          </button>
          <p className="mt-2 font-sans text-xs text-muted">Motions are due by {new Date(meeting.motionDeadline).toLocaleDateString(undefined, { dateStyle: 'medium' })} (two weeks before the meeting, §13).</p>
        </div>
      )}
      {motions.length === 0 ? (
        <p className="mt-3 font-serif text-muted">No motions yet.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {motions.map((mo) => (
            <li key={mo.id} className="rounded-md border border-border bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-serif text-base text-ink">{mo.title}</p>
                  <p className="mt-1 font-sans text-sm text-muted">{mo.body}</p>
                </div>
                <span className="rounded-full border border-border px-2.5 py-1 font-sans text-xs capitalize text-muted">{mo.status.replace('_', ' ')}</span>
              </div>
              {/* Spec 005i — a cancelled meeting is terminal: its motions render read-only (no live controls). */}
              {meeting.status !== 'cancelled' && (
                <div className="mt-2 flex gap-3">
                  {mo.status === 'submitted' && <button type="button" onClick={() => onWithdraw(mo.id)} className="font-sans text-xs text-muted underline underline-offset-2 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">Withdraw</button>}
                  {isBoard && mo.status === 'submitted' && <button type="button" onClick={() => onPlace(mo.id)} className="font-sans text-xs text-accent-deep underline underline-offset-2 hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">Place on agenda</button>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Spec 005h — the minutes record (§14.4/§19). Four states: loading (fetch in flight), success
 *  (compiled / saved / published / addendum added), specific error (verbatim server refusal), empty
 *  ("not published yet"). The published body is a FROZEN inset record — no edit/delete affordance for
 *  anyone; correction is only ever an appended, dated addendum. Trust-but-verify: the server owns every
 *  lifecycle rule; this panel reports its refusals, never re-implements them. */
function MinutesSection({ meetingId, minutes, isBoard, onChanged, flash }: {
  meetingId: string; minutes: MinutesView | null | undefined; isBoard: boolean;
  onChanged: () => Promise<void>; flash: (n: Notice) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function onCompile() {
    if (busy) return;
    setBusy(true); flash(null);
    const r = await compileMinutes(meetingId);
    setBusy(false);
    if (r.kind === 'ok') { flash({ tone: 'success', text: 'Minutes compiled.' }); void onChanged(); }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message }); // not concluded / already exist
    else flash({ tone: 'error', text: 'We couldn’t compile the minutes. Please try again.' });
  }

  return (
    <div className="mt-10">
      <h2 className="inline-flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.14em] text-ink">
        <ScrollText aria-hidden className="h-4 w-4 text-accent" /> Minutes (§14.4)
      </h2>

      {/* Loading — the by-meeting fetch hasn't resolved yet. */}
      {minutes === undefined && (
        <p className="mt-3 inline-flex items-center gap-2 font-sans text-sm text-muted" aria-live="polite">
          <Loader2 aria-hidden className="h-4 w-4 animate-spin text-accent" /> Loading minutes…
        </p>
      )}

      {/* Empty — no minutes the viewer may see (none compiled, or a draft a member can't see). */}
      {minutes === null && (
        isBoard ? (
          <div className="mt-3 rounded-md border border-border bg-surface p-4">
            <p className="font-serif text-base text-ink">The meeting has concluded. Compile the minutes from the agenda and ballot results.</p>
            <button type="button" onClick={onCompile} disabled={busy} className="mt-3 inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
              {busy ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <ScrollText aria-hidden className="h-4 w-4" />} Compile minutes
            </button>
          </div>
        ) : (
          <p className="mt-3 rounded-md border border-dashed border-border bg-surface/40 px-4 py-6 text-center font-serif text-muted">The minutes haven’t been published yet.</p>
        )
      )}

      {/* Draft (board only ever receives a draft from the server) — refine, then publish. */}
      {minutes && minutes.status === 'draft' && (
        <MinutesDraftEditor key={minutes.id} minutes={minutes} onChanged={onChanged} flash={flash} />
      )}

      {/* Published — the frozen record + its append-only addenda. Read-only for everyone. */}
      {minutes && minutes.status === 'published' && (
        <div className="mt-3">
          <p className="inline-flex items-center gap-1.5 font-sans text-xs text-muted">
            <CheckCircle2 aria-hidden className="h-3.5 w-3.5 text-accent" />
            {minutes.publishedAt ? `Published ${new Date(minutes.publishedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : 'Published'}
          </p>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-md border border-border bg-bg/40 p-4 font-serif text-sm text-ink">{minutes.body}</pre>

          {minutes.addenda.length > 0 && (
            <div className="mt-4">
              <h3 className="font-display text-xs font-semibold uppercase tracking-[0.14em] text-accent-deep">Addenda</h3>
              <ol className="mt-2 space-y-2">
                {minutes.addenda.map((a) => (
                  <li key={a.id} className="rounded-md border border-border bg-bg/40 p-3">
                    <p className="font-sans text-xs uppercase tracking-[0.12em] text-accent-deep">{new Date(a.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</p>
                    <p className="mt-1 whitespace-pre-wrap font-serif text-sm text-ink">{a.body}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {isBoard && <MinutesAddendumForm minutesId={minutes.id} onChanged={onChanged} flash={flash} />}
        </div>
      )}
    </div>
  );
}

/** Draft-only board editor: refine the compiled body, save, then publish. Keyed by minutes.id so a
 *  freshly-compiled draft mounts with its own body; there is no edit path once published. */
function MinutesDraftEditor({ minutes, onChanged, flash }: {
  minutes: MinutesView; onChanged: () => Promise<void>; flash: (n: Notice) => void;
}) {
  const [body, setBody] = useState(minutes.body);
  const [busy, setBusy] = useState(false);

  async function onSave() {
    if (busy) return;
    setBusy(true); flash(null);
    const r = await editMinutes(minutes.id, body);
    setBusy(false);
    if (r.kind === 'ok') { flash({ tone: 'success', text: 'Draft saved.' }); void onChanged(); }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message }); // immutable after publish
    else if (r.kind === 'invalid') flash({ tone: 'error', text: Object.values(r.errors)[0] ?? 'Please check the minutes text.' });
    else flash({ tone: 'error', text: 'We couldn’t save the draft. Please try again.' });
  }
  async function onPublish() {
    if (busy) return;
    setBusy(true); flash(null);
    const r = await publishMinutes(minutes.id);
    setBusy(false);
    if (r.kind === 'ok') { flash({ tone: 'success', text: 'Minutes published.' }); void onChanged(); }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message }); // already published
    else flash({ tone: 'error', text: 'We couldn’t publish the minutes. Please try again.' });
  }

  return (
    <div className="mt-3 rounded-md border border-border bg-surface p-4">
      <p className="inline-flex items-center gap-2 font-serif text-base text-ink">
        <span className="rounded bg-accent/10 px-1.5 py-0.5 font-sans text-xs font-medium text-accent-deep">Draft</span>
        Visible to the board only. Refine it, then publish to make it the permanent record.
      </p>
      <label className="sr-only" htmlFor={`minutes-body-${minutes.id}`}>Minutes text</label>
      <textarea id={`minutes-body-${minutes.id}`} maxLength={100_000} rows={12} value={body} onChange={(e) => setBody(e.target.value)} className={`${inputClass} mt-3 font-serif`} />
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={onSave} disabled={busy} className="inline-flex items-center gap-2 rounded-md border border-accent/50 bg-surface px-3.5 py-2 font-sans text-sm font-semibold text-ink hover:border-accent disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
          {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Save draft
        </button>
        <button type="button" onClick={onPublish} disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
          {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Publish minutes
        </button>
      </div>
    </div>
  );
}

/** Append-only correction on published minutes (§14.4, SC-537): the original body is never edited —
 *  a correction is a dated addendum. Submit is disabled until the field has content. */
function MinutesAddendumForm({ minutesId, onChanged, flash }: {
  minutesId: string; onChanged: () => Promise<void>; flash: (n: Notice) => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function onAdd() {
    if (busy || !body.trim()) return;
    setBusy(true); flash(null);
    const r = await addAddendum(minutesId, body.trim());
    setBusy(false);
    if (r.kind === 'ok') { setBody(''); flash({ tone: 'success', text: 'Addendum added.' }); void onChanged(); }
    else if (r.kind === 'conflict' || r.kind === 'forbidden') flash({ tone: 'error', text: r.message }); // only on published
    else if (r.kind === 'invalid') flash({ tone: 'error', text: Object.values(r.errors)[0] ?? 'An addendum can’t be empty.' });
    else flash({ tone: 'error', text: 'We couldn’t add the addendum. Please try again.' });
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      <label htmlFor={`addendum-${minutesId}`} className="font-sans text-sm text-muted">Add a correction — it’s appended and dated; the published record stays unchanged.</label>
      <textarea id={`addendum-${minutesId}`} maxLength={20_000} rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What needs correcting, and how?" className={`${inputClass} mt-2`} />
      <button type="button" onClick={onAdd} disabled={busy || !body.trim()} className="mt-3 inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
        {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Add addendum
      </button>
    </div>
  );
}

export default function MeetingDetailPage() {
  return <RequireRole min="member">{(user) => <MeetingDetailInner user={user} />}</RequireRole>;
}
